import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface PortfolioSectionProps {
  setActiveSection: (section: string) => void;
}

const PortfolioSection = ({ setActiveSection }: PortfolioSectionProps) => {
  const { ref, inView } = useInView({ 
    threshold: 0.2,
    triggerOnce: false 
  });
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    if (inView) {
      setActiveSection('portfolio');
    }
  }, [inView, setActiveSection]);

  const categories = ['all', 'images', 'videos', 'hybrid'];

  const projects = [
    {
      title: 'Luxury Brand Campaign',
      category: 'images',
      image: 'https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/eceb19a31f83891001779fea09385041.png',
      tag: 'AI Images'
    },
    {
      title: 'Custom models and Avatars',
      category: 'videos',
      image: 'https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/27dfcdb7e48daea930c61050e579fd18.png',
      tag: 'Video'
    },
    {
      title: 'Restaurant Branding',
      category: 'hybrid',
      image: 'https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/111910f3d40d6cdfe5d3797ebcbec5b6.jpeg',
      tag: 'Hybrid'
    },
    {
      title: 'Real Estate Visualization',
      category: 'images',
      image: 'https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/571c028cd7451f62adf4530273a0cf69.png',
      tag: 'AI Images'
    },
    {
      title: 'Custom Environments',
      category: 'videos',
      image: 'https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/3edd8c787c92a9bb4f14bebf502d4b52.png',
      tag: 'Video'
    },
    {
      title: 'Custom Environments',
      category: 'images',
      image: 'https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/ece7e5c284ffce90bbabf5e418c6dea5.png',
      tag: 'AI Images'
    },
    {
      title: 'Beauty Campaign',
      category: 'hybrid',
      image: 'https://public.readdy.ai/ai/img_res/ff5eaa42098ae3e4ad5dac915d7729b5.jpg',
      tag: 'Hybrid'
    },
    {
      title: 'Travel Experience',
      category: 'videos',
      image: 'https://readdy.ai/api/search-image?query=Breathtaking%20travel%20destination%20landscape%20with%20stunning%20natural%20scenery%2C%20golden%20hour%20lighting%2C%20cinematic%20composition%2C%20professional%20travel%20photography%2C%20wanderlust%20inspiring%20vista%2C%20high%20quality%20tourism%20photography&width=800&height=600&seq=portfolio-8&orientation=landscape',
      tag: 'Video'
    }
  ];

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <section
      id="portfolio"
      ref={ref}
      className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-12 bg-black"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-[3px] text-brand-aqua mb-3 font-semibold">
            OUR WORK
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6 px-4">
            Portfolio Showcase
          </h2>

          {/* Category Filter - Large tap targets */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 sm:mt-10 px-4">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-semibold capitalize transition-all whitespace-nowrap cursor-pointer min-h-[48px] ${
                  activeCategory === cat
                    ? 'bg-gradient-to-r from-brand-pink to-brand-aqua text-white'
                    : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Grid - Single column on mobile, 2 max on tablet */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={`${project.title}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="relative rounded-2xl overflow-hidden group cursor-pointer bg-white/5 aspect-[4/3]"
            >
              {/* Image - Preserve original aspect ratio */}
              <div className="w-full h-full">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-brand-pink to-brand-aqua text-white text-xs font-semibold rounded-full mb-2">
                  {project.tag}
                </span>
                <h3 className="text-base sm:text-lg font-bold text-white leading-snug">
                  {project.title}
                </h3>
              </div>

              {/* View Icon */}
              <div className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-arrow-right-up-line text-xl text-white"></i>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
